import React, { useMemo } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Row,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  ArrowLeftOutlined,
  BankOutlined,
  CarOutlined,
  FileTextOutlined,
  LineChartOutlined,
  ShoppingCartOutlined,
  ToolOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { useOrderStatusCheck } from '@/hooks/useSalesOrder';
import { useCurrency } from '@/contexts/CurrencyContext';
import { uiPlainCurrencyLabel } from '@/constants/uaDirhamDisplay';
import { EnterprisePageHeader, WorkflowStrip, WorkspaceLayout } from '@/components/enterprise';
import { useDocumentBreadcrumb } from '@/contexts/PageBreadcrumbContext';
import type {
  OrderStatusCheckInvoiceSummary,
  OrderStatusCheckResponse,
  SalesOrder,
} from '@/services/salesOrder.service';
import { useAuthStore } from '@/store/authStore';
import EstimationPerformancePanel from '@/components/sales/EstimationPerformancePanel';

const { Text } = Typography;

function usePermissionCheck() {
  const user = useAuthStore((s) => s.user);
  return (codes: string[]) =>
    Boolean(user?.is_super_admin) || codes.some((c) => (user?.permissions ?? []).includes(c));
}

const EPS = 0.01;

const SHARED_INVOICE_SHARE_HINT =
  "This invoice covers multiple orders/projects. Amounts shown are this order/project's share.";

/** Health check ignores voided sales invoices. */
function isActiveInvoice(i: { invoice_status?: string }): boolean {
  return (i.invoice_status ?? '').trim().toLowerCase() !== 'cancelled';
}

function isActiveWorkOrder(wo: { work_order_status?: string }): boolean {
  return (wo.work_order_status ?? '').trim().toLowerCase() !== 'cancelled';
}

function isActiveCustomerCreditNote(cn: { status?: string }): boolean {
  return (cn.status ?? '').trim().toLowerCase() !== 'cancelled';
}

function toNum(v: unknown): number {
  if (v === null || v === undefined) return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function attributedBilled(i: OrderStatusCheckInvoiceSummary): number {
  if (i.attributed_billed != null) return toNum(i.attributed_billed);
  return i.invoice_type === 'Advance' ? 0 : toNum(i.grand_total);
}

function attributedCollected(i: OrderStatusCheckInvoiceSummary): number {
  if (i.attributed_collected != null) return toNum(i.attributed_collected);
  return Math.max(0, toNum(i.grand_total) - toNum(i.balance_due));
}

function attributedAr(i: OrderStatusCheckInvoiceSummary): number {
  if (i.attributed_ar != null) return toNum(i.attributed_ar);
  return toNum(i.balance_due);
}

function attributedAdvance(i: OrderStatusCheckInvoiceSummary): number {
  if (i.attributed_advance_adjusted != null) return toNum(i.attributed_advance_adjusted);
  return toNum(i.advance_adjusted_amount);
}

function attributedInvoiceGross(i: OrderStatusCheckInvoiceSummary): number {
  return attributedCollected(i) + attributedAr(i);
}

function buildBlockers(payload: OrderStatusCheckResponse): string[] {
  const so = payload.sales_order;
  const { deliveries, advance_eligibility: adv, work_orders: wos } = payload;
  const invoices = payload.invoices.filter(isActiveInvoice);
  const activeInvoiceIds = new Set(invoices.map((i) => i.id));
  const wosActive = wos.filter(isActiveWorkOrder);
  const b: string[] = [];
  if (so.order_status === 'Cancelled') {
    return ['Order is cancelled.'];
  }
  if (so.order_status === 'Draft') {
    b.push('Order is still draft — confirm to proceed.');
  }
  if (so.order_status === 'In Production' && wosActive.length === 0) {
    b.push('Status is In Production but no work order is linked.');
  }
  if (so.order_status === 'Delivered' && deliveries.length === 0) {
    b.push('Order is marked delivered but no delivery note is on file.');
  }
  const stdInv = invoices.filter((i) => i.invoice_type === 'Standard');
  if (so.order_status === 'Delivered' && stdInv.length === 0) {
    b.push('Delivered but no standard tax invoice has been raised.');
  }
  const openBal = stdInv.reduce((s, i) => s + attributedAr(i), 0);
  if (stdInv.length > 0 && openBal > EPS) {
    b.push(`Outstanding AR on linked invoices: ${openBal.toFixed(2)}.`);
  }
  const receipts = (payload.receipt_allocations ?? []).filter((a) => activeInvoiceIds.has(a.sales_invoice_id));
  if (stdInv.length > 0 && openBal > EPS && receipts.length === 0) {
    b.push('Outstanding balance on invoices — no receipt voucher allocations recorded against these invoices yet.');
  }
  const today = dayjs().startOf('day');
  for (const i of stdInv) {
    if (attributedAr(i) <= EPS) continue;
    if (!i.due_date) continue;
    const due = dayjs(i.due_date).startOf('day');
    if (due.isBefore(today)) {
      b.push(
        `Invoice ${i.invoice_number} has an open balance and is past due (due ${due.format('DD MMM YYYY')}).`
      );
    }
  }
  const promised = so.promised_date;
  if (
    promised &&
    so.order_status !== 'Delivered' &&
    so.order_status !== 'Cancelled' &&
    dayjs(promised).startOf('day').isBefore(today)
  ) {
    b.push(
      `Promised date (${dayjs(promised).format('DD MMM YYYY')}) has passed and the order is not yet delivered.`
    );
  }
  if (adv.eligible && toNum(adv.remainingAdvance) > EPS) {
    b.push(
      `Advance terms apply but advance is not fully invoiced — remaining ${toNum(adv.remainingAdvance).toFixed(2)}.`
    );
  }
  return b;
}

function healthLabel(blockers: string[], so: SalesOrder): { color: string; text: string } {
  if (so.order_status === 'Cancelled') return { color: 'default', text: 'Cancelled' };
  if (blockers.length === 0) return { color: 'success', text: 'Healthy' };
  if (blockers.some((x) => x.includes('Outstanding AR') || x.includes('past due'))) {
    return { color: 'error', text: 'Financial risk' };
  }
  return { color: 'warning', text: 'Attention needed' };
}

const SalesOrderStatusCheckPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { baseCurrency } = useCurrency();
  const { data, isLoading, isError, error } = useOrderStatusCheck(id);
  const can = usePermissionCheck();

  const sym =
    uiPlainCurrencyLabel(baseCurrency?.symbol, baseCurrency?.currency_code || baseCurrency?.code) ||
    (baseCurrency?.currency_code ?? '');

  const breadcrumbSegments = useMemo(
    () =>
      data?.sales_order
        ? [
            { title: 'Growth & Revenue', path: '/growth/dashboard' },
            { title: 'Customer orders', path: '/growth/orders' },
            { title: data.sales_order.sales_order_number || 'Order', path: `/growth/orders/${data.sales_order.id}` },
            { title: 'Order health' },
          ]
        : null,
    [data?.sales_order?.id, data?.sales_order?.sales_order_number]
  );
  useDocumentBreadcrumb(breadcrumbSegments);

  const blockers = useMemo(() => (data ? buildBlockers(data) : []), [data]);

  const activeInvoices = useMemo(
    () => (data?.invoices ?? []).filter(isActiveInvoice),
    [data?.invoices]
  );
  const activeInvoiceIdSet = useMemo(() => new Set(activeInvoices.map((i) => i.id)), [activeInvoices]);
  const activeWorkOrders = useMemo(
    () => (data?.work_orders ?? []).filter(isActiveWorkOrder),
    [data?.work_orders]
  );
  const receiptAllocationsActive = useMemo(
    () => (data?.receipt_allocations ?? []).filter((a) => activeInvoiceIdSet.has(a.sales_invoice_id)),
    [data?.receipt_allocations, activeInvoiceIdSet]
  );
  const creditNoteAllocationsActive = useMemo(
    () => (data?.credit_note_allocations ?? []).filter((a) => activeInvoiceIdSet.has(a.sales_invoice_id)),
    [data?.credit_note_allocations, activeInvoiceIdSet]
  );
  const linkedCreditNotesActive = useMemo(
    () =>
      (data?.linked_credit_notes ?? []).filter(
        (cn) => isActiveCustomerCreditNote(cn) && activeInvoiceIdSet.has(cn.original_sales_invoice_id)
      ),
    [data?.linked_credit_notes, activeInvoiceIdSet]
  );

  const workflow = useMemo(() => {
    if (!data) return { stages: [] as { title: string; description?: string; status?: 'wait' | 'process' | 'finish' | 'error' }[], current: 0 };
    const so = data.sales_order;
    const { deliveries, advance_eligibility: adv } = data;
    const invoices = activeInvoices;
    const wos = activeWorkOrders;
    const hasSource = !!(so.quotation_id?.trim() || so.opportunity_id?.trim());
    const orderActive = so.order_status !== 'Draft' && so.order_status !== 'Cancelled';
    const advanceOk = !adv.eligible || toNum(adv.remainingAdvance) <= EPS;
    const hasWo = wos.length > 0;
    const hasDelivery = deliveries.length > 0 || so.order_status === 'Delivered';
    const stdInv = invoices.filter((i) => i.invoice_type === 'Standard');
    const hasStdInv = stdInv.length > 0;
    const paidOk =
      stdInv.length === 0 || stdInv.every((i) => attributedAr(i) <= EPS);

    type St = { title: string; description?: string; done: boolean; blocked?: boolean };
    const steps: St[] = [
      {
        title: 'Quotation / opportunity',
        description: hasSource ? 'Linked' : 'No quotation or opportunity on order',
        done: hasSource,
      },
      {
        title: 'Order confirmed',
        description: so.order_status,
        done: orderActive,
        blocked: so.order_status === 'Cancelled',
      },
      {
        title: 'Advance invoicing',
        description: adv.eligible
          ? advanceOk
            ? 'Advance covered'
            : `Remaining ${toNum(adv.remainingAdvance).toFixed(2)}`
          : 'Not applicable',
        done: !adv.eligible || advanceOk,
      },
      {
        title: 'Work orders',
        description: hasWo ? `${wos.length} linked` : 'None',
        done: hasWo || !['In Production', 'Ready for Delivery', 'Delivered'].includes(so.order_status),
        blocked: so.order_status === 'In Production' && !hasWo,
      },
      {
        title: 'Delivery',
        description: deliveries.length ? `${deliveries.length} note(s)` : '—',
        done: hasDelivery,
      },
      {
        title: 'Tax invoice',
        description: hasStdInv ? `${stdInv.length} standard` : 'None',
        done: hasStdInv,
      },
      {
        title: 'Payment',
        description: paidOk ? 'Settled or N/A' : 'Balance outstanding',
        done: paidOk,
      },
    ];

    let current = 0;
    for (let i = 0; i < steps.length; i++) {
      if (steps[i].blocked) {
        current = i;
        break;
      }
      if (!steps[i].done) {
        current = i;
        break;
      }
      current = i;
    }
    const allDone = steps.every((s) => s.done && !s.blocked);
    if (allDone) {
      current = steps.length;
    }

    const stages = steps.map((s, i) => {
      let status: 'wait' | 'process' | 'finish' | 'error' | undefined;
      if (s.blocked) status = 'error';
      else if (s.done) status = 'finish';
      else if (i === current) status = 'process';
      else status = 'wait';
      return { title: s.title, description: s.description, status };
    });

    return { stages, current };
  }, [data, activeInvoices, activeWorkOrders]);

  const stdInvoices = useMemo(
    () => activeInvoices.filter((i) => i.invoice_type === 'Standard'),
    [activeInvoices]
  );

  const invoicedTotal = useMemo(
    () => activeInvoices.reduce((s, i) => s + attributedBilled(i), 0),
    [activeInvoices]
  );

  const advanceSettledTotal = useMemo(
    () => activeInvoices.reduce((s, i) => s + attributedAdvance(i), 0),
    [activeInvoices]
  );

  const collectedTotal = useMemo(() => {
    const allocated = activeInvoices.reduce((s, i) => s + attributedCollected(i), 0);
    return Math.max(0, allocated - advanceSettledTotal);
  }, [activeInvoices, advanceSettledTotal]);

  const outstandingTotal = useMemo(
    () => activeInvoices.reduce((s, i) => s + attributedAr(i), 0),
    [activeInvoices]
  );

  const hasSharedInvoice = useMemo(
    () => activeInvoices.some((i) => Boolean(i.is_shared)),
    [activeInvoices]
  );

  const orderedQty = useMemo(() => {
    const items = data?.sales_order.items ?? [];
    return items.reduce((s, it) => s + toNum(it.ordered_quantity), 0);
  }, [data?.sales_order.items]);

  const deliveredQty = useMemo(() => {
    const items = data?.sales_order.items ?? [];
    return items.reduce((s, it) => s + toNum(it.delivered_quantity), 0);
  }, [data?.sales_order.items]);

  const invoicedQty = useMemo(() => {
    const items = data?.sales_order.items ?? [];
    return items.reduce((s, it) => s + toNum(it.invoiced_quantity), 0);
  }, [data?.sales_order.items]);

  const docColumns: ColumnsType<{
    key: string;
    docType: string;
    number: string;
    date?: string;
    status?: string;
    amount?: string;
    link?: string;
  }> = [
    { title: 'Type', dataIndex: 'docType', key: 'docType', width: 160 },
    { title: 'Document', dataIndex: 'number', key: 'number' },
    { title: 'Date', dataIndex: 'date', key: 'date', width: 120 },
    { title: 'Status', dataIndex: 'status', key: 'status', width: 140 },
    { title: 'Amount / qty', dataIndex: 'amount', key: 'amount', align: 'right' },
  ];

  const documentRows = useMemo(() => {
    if (!data) return [];
    const so = data.sales_order;
    const rows: {
      key: string;
      docType: string;
      number: string;
      date?: string;
      status?: string;
      amount?: string;
      link?: string;
    }[] = [];

    /** Never show a UUID as the document label. */
    const docLabel = (...candidates: Array<string | null | undefined>): string => {
      const uuidRe =
        /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      for (const c of candidates) {
        const t = String(c ?? '').trim();
        if (t && !uuidRe.test(t)) return t;
      }
      return '—';
    };

    const opp = data.opportunity_source;
    const oppId = opp?.id?.trim() || so.opportunity_id?.trim() || '';
    if (oppId) {
      rows.push({
        key: 'opp',
        docType: 'Opportunity',
        number: docLabel(opp?.opportunity_number, opp?.opportunity_name),
        status: opp?.status ?? undefined,
        link: opp?.link_path || `/sales/opportunities/${oppId}`,
      });
    }
    const qs = data.quotation_source;
    if (qs?.estimation_id) {
      rows.push({
        key: 'est',
        docType: 'Estimation',
        number: docLabel(qs.estimation_number),
        link: `/growth/estimations/edit/${qs.estimation_id}`,
      });
    }
    if (qs?.boq_id) {
      rows.push({
        key: 'boq',
        docType: 'BOQ',
        number: docLabel(qs.boq_number),
        link: `/growth/estimations/boq/edit/${qs.boq_id}`,
      });
    }
    const treId =
      so.trading_rental_estimation_id?.trim() ||
      qs?.trading_rental_estimation_id?.trim() ||
      '';
    if (treId) {
      const treCost = data.order_costing?.estimation_cost;
      rows.push({
        key: 'tre',
        docType: 'Trading / rental estimation',
        number: docLabel(
          qs?.trading_rental_estimation_number,
          data.order_costing?.source_label
        ),
        amount:
          treCost != null && treCost > 0
            ? `${sym} ${toNum(treCost).toFixed(2)} (cost)`
            : undefined,
        link: `/growth/estimations/trading-rental/${treId}`,
      });
    }
    if (so.quotation_id?.trim()) {
      rows.push({
        key: 'quo',
        docType: 'Quotation',
        number: docLabel(qs?.quotation_number),
        link: `/growth/quotations/${so.quotation_id.trim()}`,
      });
    }
    rows.push({
      key: 'so',
      docType: 'Customer order',
      number: docLabel(so.sales_order_number),
      date: dayjs(so.order_date).format('DD MMM YYYY'),
      status: so.order_status,
      amount: `${sym} ${toNum(so.grand_total).toFixed(2)}`,
      link: `/growth/orders/${so.id}`,
    });
    data.work_orders.filter(isActiveWorkOrder).forEach((wo) => {
      rows.push({
        key: `wo-${wo.id}`,
        docType: 'Work order',
        number: docLabel(wo.work_order_number),
        date: wo.order_date ? dayjs(wo.order_date as string).format('DD MMM YYYY') : undefined,
        status: wo.work_order_status,
        amount:
          wo.planned_quantity != null
            ? `${toNum(wo.produced_quantity)} / ${toNum(wo.planned_quantity)}`
            : undefined,
        link: `/mfg/work-orders/${wo.id}`,
      });
    });
    data.deliveries.forEach((d) => {
      rows.push({
        key: `dn-${d.id}`,
        docType: 'Delivery note',
        number: docLabel(d.delivery_note_number),
        date: dayjs(d.delivery_date).format('DD MMM YYYY'),
        status: d.delivery_status,
        link: `/growth/delivery-notes/${d.id}`,
      });
    });
    data.invoices.filter(isActiveInvoice).forEach((inv) => {
      rows.push({
        key: `inv-${inv.id}`,
        docType: inv.invoice_type === 'Advance' ? 'Proforma invoice' : 'Tax invoice',
        number: docLabel(inv.invoice_number),
        date: dayjs(inv.invoice_date).format('DD MMM YYYY'),
        status: `${inv.invoice_status} · ${inv.payment_status}`,
        amount: `${sym} ${attributedInvoiceGross(inv).toFixed(2)} (due ${attributedAr(inv).toFixed(2)})${
          inv.is_shared ? ' · share' : ''
        }`,
        link: `/growth/invoices/${inv.id}`,
      });
    });
    return rows;
  }, [data, sym]);

  const valueReconciliation = useMemo(() => {
    if (!data) return null;
    const so = data.sales_order;
    const inv = activeInvoices;
    const ids = activeInvoiceIdSet;
    const orderVal = toNum(so.grand_total);
    const shareOf = (invoiceId: string, fullAmount: number): number => {
      const row = inv.find((i) => i.id === invoiceId);
      if (!row) return 0;
      const g = toNum(row.grand_total);
      if (g <= 0) return 0;
      return (attributedInvoiceGross(row) / g) * fullAmount;
    };
    const advanceInvoiced = inv
      .filter((i) => i.invoice_type === 'Advance')
      .reduce((s, i) => s + attributedInvoiceGross(i), 0);
    const stdInvoiced = inv
      .filter((i) => i.invoice_type === 'Standard')
      .reduce((s, i) => s + attributedBilled(i), 0);
    const totalInvoiced = inv
      .filter((i) => i.invoice_type !== 'Advance')
      .reduce((s, i) => s + attributedBilled(i), 0);
    const receipts = (data.receipt_allocations ?? []).filter((a) => ids.has(a.sales_invoice_id));
    const receivedOnInvoices = receipts.reduce(
      (s, a) => s + shareOf(a.sales_invoice_id, toNum(a.allocated_amount)),
      0
    );
    const cnAllocs = (data.credit_note_allocations ?? []).filter((a) => ids.has(a.sales_invoice_id));
    const creditedToInvoices = cnAllocs.reduce(
      (s, a) => s + shareOf(a.sales_invoice_id, toNum(a.allocated_amount)),
      0
    );
    const outstanding = inv.reduce((s, i) => s + attributedAr(i), 0);
    return {
      orderVal,
      advanceInvoiced,
      stdInvoiced,
      totalInvoiced,
      receivedOnInvoices,
      creditedToInvoices,
      outstanding,
    };
  }, [data, activeInvoices, activeInvoiceIdSet]);

  const invDetailColumns: ColumnsType<OrderStatusCheckInvoiceSummary> = [
    {
      title: 'Invoice',
      dataIndex: 'invoice_number',
      key: 'inv',
      render: (_, r) => (
        <Space size={6} wrap>
          <Button type="link" style={{ padding: 0 }} onClick={() => navigate(`/growth/invoices/${r.id}`)}>
            {r.invoice_number}
          </Button>
          {r.is_shared ? (
            <Tooltip title={SHARED_INVOICE_SHARE_HINT}>
              <Tag color="blue">Shared</Tag>
            </Tooltip>
          ) : null}
        </Space>
      ),
    },
    { title: 'Type', dataIndex: 'invoice_type', key: 'type', width: 100 },
    {
      title: 'Due',
      dataIndex: 'due_date',
      key: 'due',
      width: 110,
      render: (v: string | undefined) => (v ? dayjs(v).format('DD MMM YYYY') : '—'),
    },
    {
      title: 'Overdue',
      key: 'od',
      width: 80,
      render: (_: unknown, r: OrderStatusCheckInvoiceSummary) => {
        if (r.invoice_type !== 'Standard' || r.invoice_status === 'Cancelled') return '—';
        if (attributedAr(r) <= EPS) return '—';
        if (!r.due_date) return '—';
        const due = dayjs(r.due_date).startOf('day');
        if (!due.isBefore(dayjs().startOf('day'))) return '—';
        return `${dayjs().startOf('day').diff(due, 'day')}d`;
      },
    },
    { title: 'Status', dataIndex: 'invoice_status', key: 'st', width: 100 },
    { title: 'Payment', dataIndex: 'payment_status', key: 'pay', width: 110 },
    {
      title: 'Invoice total',
      dataIndex: 'grand_total',
      key: 'gt',
      align: 'right',
      render: (v, r) => (
        <Tooltip title={r.is_shared ? `Full invoice ${sym} ${toNum(v).toFixed(2)}` : undefined}>
          <span>{`${sym} ${toNum(v).toFixed(2)}`}</span>
        </Tooltip>
      ),
    },
    {
      title: 'This order',
      key: 'share',
      align: 'right',
      render: (_: unknown, r: OrderStatusCheckInvoiceSummary) =>
        `${sym} ${attributedBilled(r).toFixed(2)}`,
    },
    {
      title: 'Balance due',
      key: 'bd',
      align: 'right',
      render: (_: unknown, r: OrderStatusCheckInvoiceSummary) =>
        `${sym} ${attributedAr(r).toFixed(2)}`,
    },
  ];

  if (isLoading || !id) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div style={{ padding: 24 }}>
        <Alert
          type="error"
          message="Could not load order health"
          description={(error as Error)?.message || 'Unknown error'}
          showIcon
        />
        <Button style={{ marginTop: 16 }} icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
          Back
        </Button>
      </div>
    );
  }

  const so = data.sales_order;
  const hl = healthLabel(blockers, so);

  const headerFacts = (
    <Space wrap split={<span style={{ color: '#d9d9d9' }}>|</span>}>
      <Text>
        <strong>Customer:</strong> {so.customer_name}
      </Text>
      <Text>
        <strong>Order date:</strong> {dayjs(so.order_date).format('DD MMM YYYY')}
      </Text>
      {so.promised_date && (
        <Text>
          <strong>Promised:</strong> {dayjs(so.promised_date).format('DD MMM YYYY')}
        </Text>
      )}
      <Text>
        <strong>Health:</strong>{' '}
        <Tag color={hl.color}>{hl.text}</Tag>
      </Text>
    </Space>
  );

  const primaryActions = (
    <Space wrap>
      <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(`/growth/orders/${so.id}`)}>
        Back to order
      </Button>
      <Button icon={<ShoppingCartOutlined />} onClick={() => navigate('/growth/orders')}>
        All orders
      </Button>
    </Space>
  );

  const quickActions = (
    <Space wrap>
      {so.quotation_id?.trim() && can(['crm.quotation.view', 'growth.view']) && (
        <Button icon={<FileTextOutlined />} onClick={() => navigate(`/growth/quotations/${so.quotation_id!.trim()}`)}>
          Open quotation
        </Button>
      )}
      {so.opportunity_id?.trim() && can(['crm.opportunity.view', 'growth.view']) && (
        <Button icon={<FileTextOutlined />} onClick={() => navigate(`/growth/opportunities/${so.opportunity_id!.trim()}`)}>
          Open opportunity
        </Button>
      )}
      {can(['manufacturing.view', 'growth.view']) && (
        <Button icon={<ToolOutlined />} onClick={() => navigate(`/growth/orders/${so.id}/create-work-order`)}>
          Work order from SO
        </Button>
      )}
      {can(['growth.delivery.view', 'growth.view']) && (
        <Button
          icon={<CarOutlined />}
          onClick={() => navigate(`/growth/delivery-notes?sales_order_id=${so.id}`)}
        >
          Delivery notes
        </Button>
      )}
      {can(['growth.invoice.view', 'growth.view']) && (
        <Button
          icon={<FileTextOutlined />}
          onClick={() =>
            navigate('/growth/invoices', {
              state: { salesOrderId: so.id, customerId: so.customer_id },
            })
          }
        >
          Tax invoices
        </Button>
      )}
      {can(['finance.view', 'finance.voucher.create']) && (
        <Button icon={<BankOutlined />} onClick={() => navigate('/fin/receipt-vouchers')}>
          Receipt vouchers
        </Button>
      )}
      {can(['crm.estimation.view', 'growth.view']) && data.quotation_source?.boq_id && (
        <Button
          icon={<FileTextOutlined />}
          onClick={() => navigate(`/growth/estimations/boq/edit/${data.quotation_source!.boq_id}`)}
        >
          Open BOQ
        </Button>
      )}
      {can(['crm.estimation.view', 'growth.view']) && data.quotation_source?.estimation_id && (
        <Button
          icon={<FileTextOutlined />}
          onClick={() => navigate(`/growth/estimations/edit/${data.quotation_source!.estimation_id}`)}
        >
          Open estimation
        </Button>
      )}
      {can(['crm.estimation.view', 'growth.view']) &&
        (so.trading_rental_estimation_id?.trim() ||
          data.quotation_source?.trading_rental_estimation_id?.trim()) && (
          <Button
            icon={<FileTextOutlined />}
            onClick={() =>
              navigate(
                `/growth/estimations/trading-rental/${
                  so.trading_rental_estimation_id?.trim() ||
                  data.quotation_source!.trading_rental_estimation_id!
                }`
              )
            }
          >
            Open T/R estimation
          </Button>
        )}
      {can(['finance.view']) && (
        <Button icon={<FileTextOutlined />} onClick={() => navigate('/fin/credit-notes')}>
          Customer credit notes
        </Button>
      )}
      {can(['finance.reports.view', 'growth.customer.view', 'finance.view']) && (
        <Button
          icon={<FileTextOutlined />}
          onClick={() => navigate(`/fin/reports/customer-statement?customer_id=${so.customer_id}`)}
        >
          Customer statement
        </Button>
      )}
    </Space>
  );

  return (
    <div style={{ margin: '-20px -20px 0', marginBottom: 0 }}>
      <WorkspaceLayout
        fullBleed
        header={
          <EnterprisePageHeader
            documentLabel="Order health check"
            documentNumber={so.sales_order_number}
            statusTag={
              <Tag color="processing" style={{ fontSize: '13px', padding: '2px 10px' }}>
                360° view
              </Tag>
            }
            subtitle={
              <Text type="secondary">
                <LineChartOutlined /> Lifecycle, documents, and financial snapshot
              </Text>
            }
            facts={headerFacts}
            primaryActions={primaryActions}
          />
        }
        workflow={<WorkflowStrip stages={workflow.stages} current={workflow.current} compact />}
      >
        <div style={{ padding: '0 24px 24px' }}>
          {blockers.length > 0 && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message="Blockers and attention items"
              description={
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  {blockers.map((b) => (
                    <li key={b}>{b}</li>
                  ))}
                </ul>
              }
            />
          )}

          <EstimationPerformancePanel performance={data.performance} currencySymbol={sym} />

          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            <Col xs={24} md={6}>
              <Card size="small" title="Order value">
                <Text strong style={{ fontSize: 18 }}>
                  {sym} {toNum(so.grand_total).toFixed(2)}
                </Text>
              </Card>
            </Col>
            {data.order_costing && data.order_costing.estimation_cost > 0 ? (
              <>
                <Col xs={24} md={6}>
                  <Card
                    size="small"
                    title="Customer order cost"
                    extra={
                      data.order_costing.source_label ? (
                        <Text type="secondary" style={{ fontSize: 11 }}>
                          {data.order_costing.source_label}
                        </Text>
                      ) : null
                    }
                  >
                    <Text strong style={{ fontSize: 18 }}>
                      {sym} {toNum(data.order_costing.estimation_cost).toFixed(2)}
                    </Text>
                    <Text type="secondary" style={{ display: 'block', fontSize: 12, marginTop: 4 }}>
                      From linked estimation (cost basis)
                    </Text>
                  </Card>
                </Col>
                <Col xs={24} md={6}>
                  <Card size="small" title="Gross margin">
                    <Text
                      strong
                      style={{
                        fontSize: 18,
                        color: data.order_costing.gross_profit >= 0 ? '#52c41a' : '#f5222d',
                      }}
                    >
                      {sym} {toNum(data.order_costing.gross_profit).toFixed(2)}
                    </Text>
                    <Text type="secondary" style={{ display: 'block', fontSize: 12, marginTop: 4 }}>
                      {data.order_costing.margin_pct_on_revenue.toFixed(1)}% of order value
                    </Text>
                  </Card>
                </Col>
              </>
            ) : null}
            <Col xs={24} md={6}>
              <Card size="small" title="Invoiced (excl. advance)">
                <Text strong style={{ fontSize: 18 }}>
                  {sym} {invoicedTotal.toFixed(2)}
                </Text>
              </Card>
            </Col>
            <Col xs={24} md={6}>
              <Card size="small" title="Collected (allocated)">
                <Text strong style={{ fontSize: 18 }}>
                  {sym} {collectedTotal.toFixed(2)}
                </Text>
                {advanceSettledTotal > EPS ? (
                  <Text type="secondary" style={{ display: 'block', fontSize: 12, marginTop: 4 }}>
                    Excludes advance settled on invoices ({sym}
                    {advanceSettledTotal.toFixed(2)})
                  </Text>
                ) : null}
              </Card>
            </Col>
            <Col xs={24} md={6}>
              <Card size="small" title="Outstanding (balance due)">
                <Text strong style={{ fontSize: 18 }} type={outstandingTotal > EPS ? 'danger' : undefined}>
                  {sym} {outstandingTotal.toFixed(2)}
                </Text>
              </Card>
            </Col>
          </Row>

          <Card size="small" title="Quantity snapshot (from order lines)" style={{ marginBottom: 16 }}>
            <Descriptions size="small" column={{ xs: 1, sm: 3 }}>
              <Descriptions.Item label="Ordered qty">{orderedQty.toFixed(2)}</Descriptions.Item>
              <Descriptions.Item label="Delivered qty">{deliveredQty.toFixed(2)}</Descriptions.Item>
              <Descriptions.Item label="Invoiced qty">{invoicedQty.toFixed(2)}</Descriptions.Item>
            </Descriptions>
          </Card>

          <Card size="small" title="Commercial snapshot" style={{ marginBottom: 16 }}>
            <Descriptions size="small" column={{ xs: 1, sm: 2 }}>
              {data.order_costing && data.order_costing.estimation_cost > 0 ? (
                <>
                  <Descriptions.Item label="Customer order cost (estimation)">
                    {sym} {toNum(data.order_costing.estimation_cost).toFixed(2)}
                    {data.order_costing.source_label
                      ? ` · ${data.order_costing.source_label}`
                      : ''}
                  </Descriptions.Item>
                  <Descriptions.Item label="Gross margin (value − cost)">
                    <Text
                      type={data.order_costing.gross_profit >= 0 ? 'success' : 'danger'}
                    >
                      {sym} {toNum(data.order_costing.gross_profit).toFixed(2)} (
                      {data.order_costing.margin_pct_on_revenue.toFixed(1)}%)
                    </Text>
                  </Descriptions.Item>
                </>
              ) : null}
              <Descriptions.Item label="Advance expected">
                {sym}{' '}
                {toNum(data.advance_eligibility.advancePaymentAmount).toFixed(2)}
              </Descriptions.Item>
              <Descriptions.Item label="Advance invoiced">
                {sym}{' '}
                {toNum(data.advance_eligibility.advanceInvoicedAmount).toFixed(2)}
              </Descriptions.Item>
              <Descriptions.Item label="Advance remaining to invoice">
                {sym} {toNum(data.advance_eligibility.remainingAdvance).toFixed(2)}
              </Descriptions.Item>
              <Descriptions.Item label="Standard invoices">
                {stdInvoices.length} · balance due {sym}{' '}
                {stdInvoices.reduce((s, i) => s + attributedAr(i), 0).toFixed(2)}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          {valueReconciliation && (
            <Card size="small" title="Value reconciliation (order ↔ invoices ↔ cash/credits)" style={{ marginBottom: 16 }}>
              <Descriptions size="small" column={{ xs: 1, sm: 2 }}>
                <Descriptions.Item label="Order value (SO grand total)">
                  {sym} {valueReconciliation.orderVal.toFixed(2)}
                </Descriptions.Item>
                <Descriptions.Item label="Advance invoiced (linked)">
                  {sym} {valueReconciliation.advanceInvoiced.toFixed(2)}
                </Descriptions.Item>
                <Descriptions.Item label="Standard invoiced (linked)">
                  {sym} {valueReconciliation.stdInvoiced.toFixed(2)}
                </Descriptions.Item>
                <Descriptions.Item label="Total invoiced (excl. cancelled & advance)">
                  {sym} {valueReconciliation.totalInvoiced.toFixed(2)}
                </Descriptions.Item>
                <Descriptions.Item label="Receipts allocated to these invoices">
                  {sym} {valueReconciliation.receivedOnInvoices.toFixed(2)}
                </Descriptions.Item>
                <Descriptions.Item label="Credit notes applied to these invoices">
                  {sym} {valueReconciliation.creditedToInvoices.toFixed(2)}
                </Descriptions.Item>
                <Descriptions.Item label="Sum of invoice balance due">
                  <Text type={valueReconciliation.outstanding > EPS ? 'danger' : undefined}>
                    {sym} {valueReconciliation.outstanding.toFixed(2)}
                  </Text>
                </Descriptions.Item>
              </Descriptions>
            </Card>
          )}

          <Card size="small" title="Document chain" style={{ marginBottom: 16 }}>
            <Table
              size="small"
              rowKey="key"
              columns={docColumns}
              dataSource={documentRows}
              pagination={false}
              onRow={(r) => ({
                onClick: () => {
                  if (r.link) navigate(r.link);
                },
                style: r.link ? { cursor: 'pointer' } : undefined,
              })}
            />
          </Card>

          <Card size="small" title="Invoices linked to this order" style={{ marginBottom: 16 }}>
            {hasSharedInvoice ? (
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 12 }}
                message={SHARED_INVOICE_SHARE_HINT}
              />
            ) : null}
            <Table
              size="small"
              rowKey="id"
              columns={invDetailColumns}
              dataSource={activeInvoices}
              pagination={false}
            />
          </Card>

          <Card size="small" title="Receipt vouchers (allocated to these invoices)" style={{ marginBottom: 16 }}>
            {hasSharedInvoice ? (
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 12 }}
                message="Receipts are recorded against the full invoice. Collected above uses this order's share."
              />
            ) : null}
            <Table
              size="small"
              rowKey="allocation_id"
              pagination={false}
              dataSource={receiptAllocationsActive}
              columns={[
                {
                  title: 'Receipt',
                  key: 'rv',
                  render: (_, r) => (
                    <Button
                      type="link"
                      style={{ padding: 0 }}
                      onClick={() => navigate(`/fin/receipt-vouchers/${r.receipt_voucher_id}`)}
                    >
                      {r.receipt_number}
                    </Button>
                  ),
                },
                { title: 'Date', dataIndex: 'receipt_date', key: 'rd', width: 120 },
                {
                  title: 'Invoice',
                  key: 'inv',
                  render: (_, r) => (
                    <Button type="link" style={{ padding: 0 }} onClick={() => navigate(`/growth/invoices/${r.sales_invoice_id}`)}>
                      {r.invoice_number}
                    </Button>
                  ),
                },
                {
                  title: 'Allocated',
                  dataIndex: 'allocated_amount',
                  key: 'amt',
                  align: 'right',
                  render: (v) => `${sym} ${toNum(v).toFixed(2)}`,
                },
              ]}
            />
          </Card>

          <Card size="small" title="Customer credit notes (applied to these invoices)" style={{ marginBottom: 16 }}>
            <Table
              size="small"
              rowKey="allocation_id"
              pagination={false}
              dataSource={creditNoteAllocationsActive}
              columns={[
                {
                  title: 'Credit note',
                  key: 'cn',
                  render: (_, r) => (
                    <Button
                      type="link"
                      style={{ padding: 0 }}
                      onClick={() => navigate(`/fin/credit-notes/${r.customer_credit_note_id}`)}
                    >
                      {r.credit_note_number}
                    </Button>
                  ),
                },
                { title: 'Date', dataIndex: 'credit_note_date', key: 'cd', width: 120 },
                {
                  title: 'Invoice',
                  key: 'inv',
                  render: (_, r) => (
                    <Button type="link" style={{ padding: 0 }} onClick={() => navigate(`/growth/invoices/${r.sales_invoice_id}`)}>
                      {r.invoice_number}
                    </Button>
                  ),
                },
                {
                  title: 'Applied',
                  dataIndex: 'allocated_amount',
                  key: 'amt',
                  align: 'right',
                  render: (v) => `${sym} ${toNum(v).toFixed(2)}`,
                },
              ]}
            />
          </Card>

          <Card
            size="small"
            title="Credit notes / returns (issued against invoices on this order)"
            style={{ marginBottom: 16 }}
          >
            <Table
              size="small"
              rowKey="id"
              pagination={false}
              dataSource={linkedCreditNotesActive}
              locale={{ emptyText: 'No customer credit notes linked to these invoices' }}
              columns={[
                {
                  title: 'Credit note',
                  key: 'cn',
                  render: (_, r) => (
                    <Button type="link" style={{ padding: 0 }} onClick={() => navigate(`/fin/credit-notes/${r.id}`)}>
                      {r.credit_note_number}
                    </Button>
                  ),
                },
                { title: 'Date', dataIndex: 'credit_note_date', key: 'cd', width: 120 },
                { title: 'Status', dataIndex: 'status', key: 'st', width: 100 },
                {
                  title: 'Amount',
                  dataIndex: 'total_amount',
                  key: 'amt',
                  align: 'right',
                  render: (v) => `${sym} ${toNum(v).toFixed(2)}`,
                },
                {
                  title: 'Original invoice',
                  key: 'inv',
                  render: (_, r) => (
                    <Button
                      type="link"
                      style={{ padding: 0 }}
                      onClick={() => navigate(`/growth/invoices/${r.original_sales_invoice_id}`)}
                    >
                      Open
                    </Button>
                  ),
                },
              ]}
            />
          </Card>

          <Card size="small" title="Quick actions">
            {quickActions}
          </Card>
        </div>
      </WorkspaceLayout>
    </div>
  );
};

export default SalesOrderStatusCheckPage;
